import React from 'react'
import { Activity, Building2, CalendarDays, Hand, Puzzle, ScrollText, Shield, Sparkles } from 'lucide-react'
import Reveal3 from '@/components/marketing3/Reveal3'
import { cn } from '@/lib/utils'

/**
 * IncludedGrid3 — light, quick-scan "everything included" grid. Eight tiles, one line
 * each (no screenshot), on the daylight editorial surface. Every string is VERBATIM from
 * the copy doc's §Everything included; tiles reveal on scroll with a staggered entrance.
 *
 * The two v1 tiles Portfolio + Team roles are one tile here (plan §2.9 — eight tiles).
 * No PMS name, no cadence, no plan/packaging talk (§0 do-not-publish list).
 */

const container = 'mx-auto max-w-6xl px-4 sm:px-6'

const TILES: { icon: React.ElementType; title: string; body: string }[] = [
  {
    icon: CalendarDays,
    title: 'Rate Calendar',
    body: 'Your daily command centre — every price, every day, with the full Ledger one hover away.',
  },
  {
    icon: Activity,
    title: 'Demand Response',
    body: 'Reads your real booking pace against your own history — property-wide and per room type — at your chosen sensitivity.',
  },
  {
    icon: Shield,
    title: 'Price Guardrails',
    body: 'Your safety net — floor and ceiling limits that keep rates within acceptable bounds.',
  },
  {
    icon: Puzzle,
    title: 'Gap Filling',
    body: 'Turn hard-to-sell single-night gaps between bookings into revenue.',
  },
  {
    icon: Sparkles,
    title: 'Events',
    body: 'Capture demand spikes from local events your booking history can’t predict.',
  },
  {
    icon: Hand,
    title: 'Overrides',
    body: 'You always have the last word — an override beats the engine, every time.',
  },
  {
    icon: ScrollText,
    title: 'Change Log',
    body: 'Every rate change recorded — trace what changed, when, and why.',
  },
  {
    icon: Building2,
    title: 'Portfolio & team roles',
    body: 'Run one B&B or a whole portfolio from one login — with the right level of access for every team member.',
  },
]

export default function IncludedGrid3({ brand }: { brand: string }) {
  return (
    <section id="included" aria-label={`What’s included in ${brand}`} className="border-t border-stone-200/70">
      <div className={cn(container, 'py-24 sm:py-28')}>
        <Reveal3 className="max-w-3xl">
          <p className="mkt3-eyebrow">Everything included</p>
          <h2 className="mkt3-display mkt3-h2 mt-4">The whole cockpit, not just a price feed.</h2>
        </Reveal3>

        <ul className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {TILES.map((tile, i) => (
            <Reveal3 as="li" key={tile.title} delay={(i % 4) * 70}>
              <div className="mkt3-lift h-full rounded-xl border border-stone-200/80 bg-white p-5 shadow-sm">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-700">
                  <tile.icon className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>
                <h3 className="mt-3.5 text-sm font-semibold text-slate-900">{tile.title}</h3>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{tile.body}</p>
              </div>
            </Reveal3>
          ))}
        </ul>
      </div>
    </section>
  )
}
